/**
 * Authentication wrapper to handle seamless guest-to-authenticated user transitions
 */

import { useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '../store';
import { handleGuestToUserTransition } from './userTransition';
import { useLicenseStore } from '../store/licenseStore';

/**
 * Hook to manage authentication state changes and session continuity
 */
export const useAuthenticationManager = () => {
  const { isSignedIn, user, isLoaded } = useUser();
  const navigate = useNavigate();
  const userAuthState = useSessionStore((state) => state.userAuthState);
  const restoreSessionContinuity = useSessionStore((state) => state.restoreSessionContinuity);
  const setUserAuthState = useSessionStore((state) => state.setUserAuthState);

  // Shop items are public and gate the editor picker — load once on app mount
  // so the lock state is correct on first render for both signed-in and guest users.
  useEffect(() => {
    useLicenseStore.getState().loadShopItems();
  }, []);

  useEffect(() => {
    if (!isLoaded) return; // Wait for Clerk to load

    const handleAuthStateChange = async () => {
      if (isSignedIn && user) {
        // User is now authenticated
        if (userAuthState === 'guest' || userAuthState === 'signing_in') {
          console.log('User authenticated, handling session continuity...');

          // Restore session continuity
          const continuitySessionId = restoreSessionContinuity();
          console.log('Session continuity restored with ID:', continuitySessionId);

          // Handle the guest-to-user transition
          try {
            const transitionResult = await handleGuestToUserTransition(user);
            console.log('Authentication transition completed:', transitionResult);

            // Notify user if rooms were migrated
            if (transitionResult?.migratedCount > 0) {
              console.log(`✅ Successfully migrated ${transitionResult.migratedCount} guest room(s) to your account!`);
              // Could show a toast notification here if you have a toast system
            }
          } catch (error) {
            console.error('Authentication transition failed:', error);
          } finally {
            // Always clear the migration flag so EditorPage can proceed
            useSessionStore.getState().setIsMigrating(false);

            // Navigate to the pending redirect if one was stored (handles OAuth flows
            // like Google Sign-In where Clerk's redirectUrl is dropped during the
            // OAuth redirect cycle)
            const pendingRedirect = useSessionStore.getState().pendingAuthRedirect;
            if (pendingRedirect) {
              useSessionStore.getState().clearPendingAuthRedirect();
              navigate(pendingRedirect);
            }
          }

          // Mark user as authenticated
          setUserAuthState('authenticated');

          // Load licenses for this user
          useLicenseStore.getState().loadLicenses();
        }
      } else if (!isSignedIn && userAuthState === 'authenticated') {
        // User signed out
        console.log('User signed out, resetting to guest state');
        setUserAuthState('guest');
        useLicenseStore.getState().clearLicenses();
        // Keep the session ID for continuity in case they sign back in
      } else if (!isSignedIn && userAuthState !== 'guest') {
        // Default to guest state
        setUserAuthState('guest');
      }
    };

    handleAuthStateChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, user, isLoaded, userAuthState, restoreSessionContinuity, setUserAuthState]);

  // Provide helper functions for components
  const preserveSessionForAuth = useSessionStore((state) => state.preserveSessionForAuth);
  const currentSessionId = useSessionStore((state) => state.currentSessionId);

  const startAuthentication = () => {
    console.log('Starting authentication, preserving session...');
    preserveSessionForAuth();
  };

  const getCurrentUserInfo = () => {
    return {
      isSignedIn,
      user,
      authState: userAuthState,
      sessionId: currentSessionId
    };
  };

  return {
    isSignedIn,
    user,
    isLoaded,
    authState: userAuthState,
    sessionId: currentSessionId,
    startAuthentication,
    getCurrentUserInfo
  };
};

/**
 * Get current user info for API calls with session continuity
 */
export const getCurrentUserInfo = () => {
  const sessionStore = useSessionStore.getState();

  // Try to get Clerk user info if available
  // Note: This won't work outside of React components, so components should pass userInfo
  return {
    sessionId: sessionStore.currentSessionId || sessionStore.getOrCreateSessionId(),
    authState: sessionStore.userAuthState
  };
};
